import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { shipmentStorageService } from '../../../services/shipmentStorageService';
import { supabase } from '../../../lib/supabase';

const DocumentUploadSection = ({ shipmentId, documentUrl, onDocumentUploaded }) => {
  const [documents, setDocuments] = useState([]);
  const [extraDocumentsUrls, setExtraDocumentsUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadingMain, setUploadingMain] = useState(false);
  const [uploadingExtra, setUploadingExtra] = useState(false);
  const [selectedType, setSelectedType] = useState('invoice');
  const [loading, setLoading] = useState(true);

  // Edit modal state
  const [editingDoc, setEditingDoc] = useState(null);
  const [editName, setEditName] = useState('');
  const [editFile, setEditFile] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const mainDocInputRef = useRef(null);
  const extraDocInputRef = useRef(null);
  const editFileInputRef = useRef(null);

  useEffect(() => {
    if (shipmentId) {
      loadDocuments();
      loadExtraDocuments();
    } else {
      setLoading(false);
    }
  }, [shipmentId]);

  const loadDocuments = async () => {
    if (!shipmentId) {
      console.warn('Cannot load documents: shipmentId is undefined');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const docs = await shipmentStorageService?.getDocuments(shipmentId);
      setDocuments(docs);
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadExtraDocuments = async () => {
    try {
      const { data: shipment, error } = await supabase
        ?.from('shipments')
        ?.select('extra_documents_urls')
        ?.eq('id', shipmentId)
        ?.single();

      if (error) throw error;

      if (Array.isArray(shipment?.extra_documents_urls)) {
        const urlPromises = shipment?.extra_documents_urls?.map(async (path) => {
          const url = await shipmentStorageService?.getDocumentUrl(path);
          return { path, url };
        });
        const docs = await Promise.all(urlPromises);
        setExtraDocumentsUrls(docs?.filter(d => d?.url !== null));
      }
    } catch (error) {
      console.error('Error loading extra documents:', error);
    }
  };

  const handleMainDocumentUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    if (!shipmentId) {
      alert('Erreur: ID d\'envoi manquant. Impossible de téléverser le document.');
      return;
    }

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes?.includes(file?.type)) {
      alert('Format non supporté. Veuillez choisir un fichier PDF, JPG, JPEG ou PNG.');
      return;
    }

    try {
      setUploadingMain(true);
      const { filePath } = await shipmentStorageService?.uploadDocument(shipmentId, file, 'main');
      const signedUrl = await shipmentStorageService?.getDocumentUrl(filePath);
      const { error: updateError } = await supabase
        ?.from('shipments')
        ?.update({ document_url: signedUrl })
        ?.eq('id', shipmentId);

      if (updateError) throw updateError;

      alert('Document principal téléversé avec succès!');
      await loadDocuments();
      if (onDocumentUploaded) onDocumentUploaded();
    } catch (error) {
      console.error('Error uploading main document:', error);
      alert('Erreur lors du téléversement: ' + error?.message);
    } finally {
      setUploadingMain(false);
      e.target.value = '';
    }
  };

  const handleExtraDocumentUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    if (!shipmentId) {
      alert('Erreur: ID d\'envoi manquant. Impossible de téléverser le document.');
      return;
    }

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes?.includes(file?.type)) {
      alert('Format non supporté. Veuillez choisir un fichier PDF, JPG, JPEG ou PNG.');
      return;
    }

    try {
      setUploadingExtra(true);
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${selectedType}-${shipmentId}-${Date.now()}.${fileExt}`;
      const filePath = `${shipmentId}/${fileName}`;

      const { error: uploadError } = await supabase
        ?.storage
        ?.from('shipment-documents')
        ?.upload(filePath, file, { cacheControl: '3600', upsert: false });

      if (uploadError) throw uploadError;

      const { data: shipment, error: fetchError } = await supabase
        ?.from('shipments')
        ?.select('extra_documents_urls')
        ?.eq('id', shipmentId)
        ?.single();

      if (fetchError) throw fetchError;

      const currentDocs = Array.isArray(shipment?.extra_documents_urls) ? shipment?.extra_documents_urls : [];
      const updatedDocs = [...currentDocs, filePath];

      const { error: updateError } = await supabase
        ?.from('shipments')
        ?.update({ extra_documents_urls: updatedDocs })
        ?.eq('id', shipmentId);

      if (updateError) throw updateError;

      alert('Document supplémentaire téléversé avec succès!');
      await loadExtraDocuments();
      if (onDocumentUploaded) onDocumentUploaded();
    } catch (error) {
      console.error('Error uploading extra document:', error);
      alert('Erreur lors du téléversement: ' + error?.message);
    } finally {
      setUploadingExtra(false);
      e.target.value = '';
    }
  };

  const handleDeleteExtraDocument = async (docPath) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce document?')) return;

    try {
      await shipmentStorageService?.deleteDocument(docPath);

      const { data: shipment, error: fetchError } = await supabase
        ?.from('shipments')
        ?.select('extra_documents_urls')
        ?.eq('id', shipmentId)
        ?.single();

      if (fetchError) throw fetchError;

      const currentDocs = Array.isArray(shipment?.extra_documents_urls) ? shipment?.extra_documents_urls : [];
      const updatedDocs = currentDocs?.filter(d => d !== docPath);

      const { error: updateError } = await supabase
        ?.from('shipments')
        ?.update({ extra_documents_urls: updatedDocs })
        ?.eq('id', shipmentId);

      if (updateError) throw updateError;

      alert('Document supprimé avec succès!');
      await loadExtraDocuments();
      if (onDocumentUploaded) onDocumentUploaded();
    } catch (error) {
      console.error('Error deleting extra document:', error);
      alert('Erreur lors de la suppression: ' + error?.message);
    }
  };

  const handleDelete = async (docId, fileUrl) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce document?')) return;

    try {
      await shipmentStorageService?.deleteDocument(fileUrl);

      const { error } = await supabase
        ?.from('shipment_files')
        ?.delete()
        ?.eq('id', docId);

      if (error) throw error;

      alert('Document supprimé avec succès!');
      await loadDocuments();
      if (onDocumentUploaded) onDocumentUploaded();
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Erreur lors de la suppression: ' + error?.message);
    }
  };

  // Open edit modal
  const openEditModal = (doc) => {
    setEditingDoc(doc);
    setEditName(doc?.file_name || '');
    setEditFile(null);
    if (editFileInputRef?.current) editFileInputRef.current.value = '';
  };

  const closeEditModal = () => {
    setEditingDoc(null);
    setEditName('');
    setEditFile(null);
  };

  const handleEditSave = async () => {
    if (!editingDoc) return;
    if (!editName?.trim()) {
      alert('Le nom du document ne peut pas être vide.');
      return;
    }

    try {
      setEditSaving(true);

      let updatePayload = { file_name: editName?.trim() };

      // If a new file is selected, replace the file in storage
      if (editFile) {
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
        if (!allowedTypes?.includes(editFile?.type)) {
          alert('Format non supporté. Veuillez choisir un fichier PDF, JPG, JPEG ou PNG.');
          setEditSaving(false);
          return;
        }

        // Determine bucket and path from existing file_url or bucket_name
        const bucket = editingDoc?.bucket_name || 'shipment-documents';
        const fileExt = editFile?.name?.split('.')?.pop();
        const newFileName = `${editingDoc?.file_type || 'doc'}-${shipmentId}-${Date.now()}.${fileExt}`;
        const newFilePath = `${shipmentId}/${newFileName}`;

        // Upload new file
        const { error: uploadError } = await supabase
          ?.storage
          ?.from(bucket)
          ?.upload(newFilePath, editFile, { cacheControl: '3600', upsert: false });

        if (uploadError) throw uploadError;

        // Get signed URL for new file
        const { data: signedData } = await supabase
          ?.storage
          ?.from(bucket)
          ?.createSignedUrl(newFilePath, 3600 * 24 * 7);

        // Try to delete old file (non-blocking)
        if (editingDoc?.file_url) {
          try {
            await shipmentStorageService?.deleteDocument(editingDoc?.file_url);
          } catch (_) {}
        }

        updatePayload.file_url = newFilePath;
        updatePayload.bucket_name = bucket;
        if (signedData?.signedUrl) {
          updatePayload.file_url = newFilePath;
        }
      }

      // Update shipment_files record
      const { error: updateError } = await supabase
        ?.from('shipment_files')
        ?.update(updatePayload)
        ?.eq('id', editingDoc?.id);

      if (updateError) throw updateError;

      alert('Document modifié avec succès!');
      closeEditModal();
      await loadDocuments();
      if (onDocumentUploaded) onDocumentUploaded();
    } catch (error) {
      console.error('Error editing document:', error);
      alert('Erreur lors de la modification: ' + error?.message);
    } finally {
      setEditSaving(false);
    }
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      'invoice': 'Facture',
      'douane': 'Documents douaniers',
      'main': 'Document principal',
      'autre': 'Autre document'
    };
    return labels?.[type] || type;
  };

  const getDocumentIcon = (type) => {
    const icons = {
      'invoice': 'Receipt',
      'douane': 'FileText',
      'main': 'FileCheck',
      'autre': 'File'
    };
    return icons?.[type] || 'File';
  };

  if (!shipmentId) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="FileText" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Documents</h3>
              <p className="text-sm text-muted-foreground">Gérer les documents de l'envoi</p>
            </div>
          </div>
        </div>
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 text-center">
          <Icon name="AlertTriangle" size={32} className="text-yellow-600 mx-auto mb-2" />
          <p className="text-sm text-yellow-600">
            L'envoi doit être créé avant de pouvoir ajouter des documents.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="FileText" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Documents</h3>
            <p className="text-sm text-muted-foreground">Gérer les documents de l'envoi</p>
          </div>
        </div>
      </div>
      {/* Main Document Upload Section */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
        <div className="flex items-center space-x-2 mb-2">
          <Icon name="FileCheck" size={18} className="text-blue-600" />
          <h4 className="text-sm font-semibold text-blue-900">Document principal</h4>
        </div>

        {documentUrl ? (
          <div className="flex items-center justify-between p-3 bg-white border border-blue-200 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
                <Icon name="FileCheck" size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Document principal disponible</p>
                <p className="text-xs text-gray-500">Cliquez sur télécharger pour voir le document</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <a href={documentUrl} target="_blank" rel="noopener noreferrer" download>
                <Button variant="ghost" size="sm" iconName="Download" />
              </a>
            </div>
          </div>
        ) : (
          <p className="text-xs text-gray-600">Aucun document principal téléversé</p>
        )}

        <input
          type="file"
          ref={mainDocInputRef}
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={handleMainDocumentUpload}
          disabled={uploadingMain}
          className="hidden"
        />
        <label htmlFor="main-document-upload" className="block cursor-pointer">
          <Button
            as="span"
            variant="outline"
            fullWidth
            iconName={uploadingMain ? 'Loader' : 'Upload'}
            iconPosition="left"
            disabled={uploadingMain}
            className="cursor-pointer hover:bg-primary/10 transition-colors"
            onClick={() => mainDocInputRef?.current?.click()}
          >
            {uploadingMain ? 'Téléversement en cours...' : documentUrl ? 'Remplacer le document principal' : 'Téléverser le document principal'}
          </Button>
        </label>

        <p className="text-xs text-blue-700 text-center">
          Document principal de l'envoi (PDF, JPG, JPEG, PNG - max 20MB)
        </p>
      </div>
      {/* Additional Documents Upload Section */}
      <div className="mb-6 p-4 bg-muted/30 rounded-lg space-y-3">
        <div className="flex items-center space-x-2 mb-2">
          <Icon name="Files" size={18} className="text-gray-600" />
          <h4 className="text-sm font-semibold text-gray-900">Documents supplémentaires</h4>
        </div>

        {extraDocumentsUrls?.length > 0 && (
          <div className="space-y-2 mb-4">
            {extraDocumentsUrls?.map((doc, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-white border border-border rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
                    <Icon name="FileText" size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      Document supplémentaire {index + 1}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Cliquez sur télécharger pour voir le document
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <a href={doc?.url} target="_blank" rel="noopener noreferrer" download>
                    <Button variant="ghost" size="sm" iconName="Download" />
                  </a>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Trash2"
                    onClick={() => handleDeleteExtraDocument(doc?.path)}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Type de document
          </label>
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e?.target?.value)}
            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
            disabled={uploadingExtra}
          >
            <option value="invoice">Facture</option>
            <option value="douane">Documents douaniers</option>
            <option value="cni">Copie CNI</option>
            <option value="autorisation">Autorisation</option>
            <option value="autre">Autre</option>
          </select>
        </div>

        <input
          type="file"
          ref={extraDocInputRef}
          accept="application/pdf,.jpg,.jpeg,.png"
          onChange={handleExtraDocumentUpload}
          disabled={uploadingExtra}
          className="hidden"
        />
        <label htmlFor="extra-document-upload" className="block cursor-pointer">
          <Button
            as="span"
            variant="outline"
            fullWidth
            iconName={uploadingExtra ? 'Loader' : 'Upload'}
            iconPosition="left"
            disabled={uploadingExtra}
            className="cursor-pointer hover:bg-primary/10 transition-colors"
            onClick={() => extraDocInputRef?.current?.click()}
          >
            {uploadingExtra ? 'Téléversement en cours...' : 'Téléverser un document supplémentaire'}
          </Button>
        </label>

        <p className="text-xs text-muted-foreground text-center">
          Format accepté: PDF, JPG, JPEG, PNG (max 20MB)
        </p>
      </div>
      {/* Documents from shipment_files table */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-foreground">Documents existants</h4>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : documents?.length > 0 ? (
          documents?.map((doc) => (
            <div
              key={doc?.id}
              className="flex items-center justify-between p-4 bg-muted/30 border border-border rounded-lg"
            >
              <div className="flex items-center space-x-3 min-w-0 flex-1">
                <div className="w-10 h-10 flex-shrink-0 bg-red-500/10 rounded-lg flex items-center justify-center">
                  <Icon name={getDocumentIcon(doc?.file_type)} size={20} className="text-red-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {doc?.file_name || getDocumentTypeLabel(doc?.file_type)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {getDocumentTypeLabel(doc?.file_type)} • {new Date(doc.created_at)?.toLocaleDateString('fr-FR')}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-1 flex-shrink-0 ml-2">
                <a href={doc?.downloadUrl} target="_blank" rel="noopener noreferrer" download>
                  <Button variant="ghost" size="sm" iconName="Download" title="Télécharger" />
                </a>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Pencil"
                  onClick={() => openEditModal(doc)}
                  title="Modifier"
                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Trash2"
                  onClick={() => handleDelete(doc?.id, doc?.file_url)}
                  title="Supprimer"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                />
              </div>
            </div>
          ))
        ) : (
          <div className="bg-muted/30 border border-border/50 rounded-lg p-8 text-center">
            <Icon name="FileX" size={48} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Aucun document supplémentaire</p>
          </div>
        )}
      </div>
      {/* Edit Document Modal */}
      {editingDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Icon name="Pencil" size={18} className="text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Modifier le document</h3>
              </div>
              <button
                onClick={closeEditModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={editSaving}
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            {/* Rename */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom du document
              </label>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Nom du fichier"
                disabled={editSaving}
              />
            </div>

            {/* Replace file */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Remplacer le fichier <span className="text-gray-400 font-normal">(optionnel)</span>
              </label>
              <input
                type="file"
                ref={editFileInputRef}
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setEditFile(e?.target?.files?.[0] || null)}
                disabled={editSaving}
                className="hidden"
              />
              <div
                className="flex items-center space-x-3 p-3 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
                onClick={() => !editSaving && editFileInputRef?.current?.click()}
              >
                <Icon name="Upload" size={18} className="text-gray-400" />
                <span className="text-sm text-gray-500">
                  {editFile ? editFile?.name : 'Cliquez pour choisir un nouveau fichier'}
                </span>
              </div>
              {editFile && (
                <button
                  onClick={() => { setEditFile(null); if (editFileInputRef?.current) editFileInputRef.current.value = ''; }}
                  className="mt-1 text-xs text-red-500 hover:text-red-700"
                  disabled={editSaving}
                >
                  Annuler le remplacement
                </button>
              )}
            </div>

            <div className="flex space-x-3">
              <Button
                variant="outline"
                fullWidth
                onClick={closeEditModal}
                disabled={editSaving}
              >
                Annuler
              </Button>
              <Button
                variant="primary"
                fullWidth
                iconName={editSaving ? 'Loader' : 'Save'}
                iconPosition="left"
                onClick={handleEditSave}
                disabled={editSaving}
              >
                {editSaving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentUploadSection;